/**
 * The CRM tag library — the tags themselves, as the settings screen manages
 * them, rather than the trimmed shape that rides along on a contact or company.
 *
 * Mirrors the `/crm/tags` contract (CRM_Tags_API-V2.md). Two things read
 * differently here than in the design frame, because the API is the one that
 * has to be satisfied:
 *
 *  - `appliesTo` is a single enum (`CONTACT` / `COMPANY` / `BOTH`), not a list.
 *    The modal still asks it as two checkboxes, so the pair of converters below
 *    does the translating at the edge.
 *  - `colorKey` is the colour itself, not a key into anything — see below.
 */

/** A tag out of use is made Inactive rather than deleted: records still carry it. */
export type CrmTagStatus = "ACTIVE" | "INACTIVE";

/** Where a tag may be used, as the API stores it. */
export type CrmTagAppliesTo = "CONTACT" | "COMPANY" | "BOTH";

/** One kind of record a tag may be put on — what the modal ticks. */
export type CrmTagTarget = "CONTACT" | "COMPANY";

/**
 * A `#RRGGBB` colour — six hex digits, leading `#`, returned upper-case.
 *
 * The field is still called `colorKey` on the wire for API stability, but it
 * is no longer a key: there is no palette to look it up in, and nothing here
 * maps it. Use it as the chip/dot colour directly.
 */
export type CrmTagColor = string;

/** A row of the tag library — `CrmTagListItem` on the wire. */
export interface CrmTag {
  uuid: string;
  name: string;
  /** The colour itself, `#RRGGBB`. Never null — legacy tags were back-filled. */
  colorKey: CrmTagColor;
  appliesTo: CrmTagAppliesTo;
  /** Contacts + Companies currently tagged with it. Deals are not counted. */
  usageCount: number;
  status: CrmTagStatus;
}

/**
 * A tag as the assignable list returns it — the same thing minus the count,
 * which that endpoint does not calculate.
 */
export type CrmAssignableTag = Omit<CrmTag, "usageCount">;

/** One record using a tag, as the usage view lists it. */
export interface CrmTagUsageRecord {
  uuid: string;
  /**
   * The human-readable key (`CON-12`, `TECH-3`).
   *
   * This is what navigation uses, not `uuid`: the Contact and Company detail
   * routes are `/crm/contacts/:contactKey` and `/crm/companies/:companyKey`,
   * and they resolve the key to a uuid themselves.
   */
  key: string;
  /** Contact: first + middle + last. Company: company name. */
  name: string;
  /**
   * Contact: email, falling back to job title. Company: industry.
   * Absent when there is nothing to show — omit the line rather than print a gap.
   */
  subtitle?: string | null;
}

/**
 * GET /crm/tags/{tagUuid}/usage — where a tag is currently used.
 *
 * Read-only, and always the live picture. Deals are deliberately absent: they
 * carry tags but are not counted here (§8).
 */
export interface CrmTagUsage {
  tagUuid: string;
  tagName: string;
  /** `contactCount + companyCount`. Always exact, even when the lists are capped. */
  total: number;
  contactCount: number;
  companyCount: number;
  contacts: CrmTagUsageRecord[];
  companies: CrmTagUsageRecord[];
}

/** Each list is capped; the counts above are not. */
export const CRM_TAG_USAGE_LIST_LIMIT = 500;

/** Body of both create and edit — the edit is a full replace of the same three. */
export interface CrmTagPayload {
  name: string;
  appliesTo: CrmTagAppliesTo;
  colorKey: CrmTagColor;
}

export interface GetCrmTagsParams {
  search?: string;
  /** Omit for "All" — `ALL` is not an enum value and the API answers it with a 500. */
  status?: CrmTagStatus | null;
  page?: number;
  size?: number;
}

/** What the API accepts: `#` then exactly six hex digits. No shorthand, no names. */
export const CRM_TAG_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;

/**
 * The backend's own fallback — what deal-created tags and back-filled legacy
 * tags carry, and what this screen falls back to when a colour is unreadable.
 */
export const CRM_TAG_DEFAULT_COLOR = "#6B7280";

/**
 * Starting points for the colour picker, nothing more.
 *
 * The API takes any valid hex now, so this is a frontend convenience rather
 * than a palette: it saves an admin from typing a hex for the common cases.
 * The values are the ones the design frame used.
 */
export const CRM_TAG_SWATCHES: CrmTagColor[] = [
  "#8B5CF6",
  "#3B82F6",
  "#6366F1",
  "#06B6D4",
  "#10B981",
  "#EC9E12",
  "#ED6A1C",
  "#DF3145",
  "#DE2F96",
  CRM_TAG_DEFAULT_COLOR,
];

export const isCrmTagColor = (value: unknown): value is CrmTagColor =>
  typeof value === "string" && CRM_TAG_COLOR_PATTERN.test(value);

/**
 * A colour safe to hand to CSS, upper-cased the way the API returns it.
 *
 * Anything unreadable becomes grey rather than an empty `background-color`,
 * which would leave an invisible dot behind.
 */
export const normalizeCrmTagColor = (value: unknown): CrmTagColor =>
  isCrmTagColor(value) ? value.toUpperCase() : CRM_TAG_DEFAULT_COLOR;

/**
 * Inline style for a tag chip in the tag's own colour: a tint behind, a
 * firmer border, the colour itself for the text. A tint rather than a solid
 * fill, so a pale tag stays readable against the dark UI.
 */
export const tagChipStyle = (color: unknown) => {
  const hex = normalizeCrmTagColor(color);
  return {
    backgroundColor: `${hex}22`,
    borderColor: `${hex}59`,
    color: hex,
  };
};

export const CRM_TAG_TARGET_LABELS: Record<CrmTagTarget, string> = {
  CONTACT: "Contacts",
  COMPANY: "Companies",
};

/** `BOTH` becomes two chips, so the table reads the same as the modal's ticks. */
export const appliesToTargets = (appliesTo: CrmTagAppliesTo): CrmTagTarget[] =>
  appliesTo === "BOTH" ? ["CONTACT", "COMPANY"] : [appliesTo];

/** The modal's ticks, back to the enum. `null` when nothing is ticked. */
export const targetsToAppliesTo = (
  targets: CrmTagTarget[],
): CrmTagAppliesTo | null => {
  const contact = targets.includes("CONTACT");
  const company = targets.includes("COMPANY");

  if (contact && company) return "BOTH";
  if (contact) return "CONTACT";
  if (company) return "COMPANY";
  return null;
};
